/**
 * "The build this page came from is gone."
 *
 * Filenames carry a content hash, so every deploy renames every chunk. A tab
 * that was open across a release is holding an index that points at files which
 * now 404, and the next `React.lazy` import rejects. That is a routine
 * consequence of shipping, not a bug, and it is the single most likely failure
 * on any code-split route.
 *
 * Deliberately dependency-free and deliberately NOT co-located with either
 * boundary that uses it. One lives in the admin chunk and one has to live in
 * the main bundle, so a shared module is the only way the two can be certain to
 * classify an error the same way — and mis-classifying is not cosmetic here,
 * because the two branches offer opposite recoveries.
 *
 * Matched on the constructor name first, which webpack sets, with the messages
 * as fallbacks for the same failure raised by a native dynamic import or by a
 * bundler that does not set the name.
 */
export const isChunkLoadFailure = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message)
  );
};

/**
 * Shared so the two boundaries cannot drift into describing the same event
 * differently — a reader who hits it in the shell and again inside the console
 * should not have to work out whether they are two different problems.
 *
 * "Reload", never "Retry": retrying in place cannot work. React caches a
 * `lazy()` rejection, so the same import is never re-attempted and a Retry
 * button would do nothing at all while looking like it should.
 */
export const STALE_BUILD_COPY = {
  title: 'This page is out of date',
  detail: 'A new version shipped while the console was open. Reload to pick it up.',
  action: 'Reload the console',
} as const;
